import { type ReactNode, useMemo } from 'react'

import { Icon } from '@/components/icons/Icon'

type Props = {
  padding?: number
  pageNumber?: number
  totalPages?: number
}

type PaginationOptionsType = {
  disabled?: boolean
  isSelected?: boolean
  label: ReactNode
  title?: string
  value: number
}

export const usePagination = ({ padding = 1, pageNumber = 1, totalPages = 1 }: Props) =>
  useMemo<PaginationOptionsType[]>(() => {
    // Never show ellipsis when it would be less than the total number of pages anyway
    const maxOptions = padding * 4 + 3
    const showAllPages = totalPages <= maxOptions

    const startPage = showAllPages ? 1 : Math.max(1, pageNumber - padding)
    const endPage = showAllPages ? totalPages : Math.min(totalPages, pageNumber + padding)

    return [
      ...(startPage > 1
        ? [
            {
              isSelected: pageNumber === 1,
              label: 1,
              title: 'Page Number: 1',
              value: 1,
            },
          ]
        : []),
      ...(startPage > 2
        ? [
            {
              disabled: true,
              label: <Icon name='ellipsis' />,
              value: 0,
            },
          ]
        : []),
      ...Array.from({ length: endPage - startPage + 1 }, (_, i) => {
        const page = startPage + i
        return {
          isSelected: pageNumber === page,
          label: String(page),
          title: `Page Number: ${page}`,
          value: page,
        }
      }),
      ...(endPage + 1 < totalPages
        ? [
            {
              disabled: true,
              label: <Icon name='ellipsis' />,
              value: 0,
            },
          ]
        : []),
      ...(endPage < totalPages
        ? [
            {
              isSelected: pageNumber === totalPages,
              label: totalPages,
              title: `Page Number: ${totalPages}`,
              value: totalPages,
            },
          ]
        : []),
    ]
  }, [padding, pageNumber, totalPages])
